import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Package, Loader2, Search, Filter, LayoutGrid, List } from 'lucide-react';
import { inventoryAPI } from '../api';

/**
 * Modal for selecting products from inventory with infinite scroll
 * Used in TryOn Studio, Social Page, and Analytics for product selection
 * 
 * Features:
 * - Gender-based filtering (default: all)
 * - Search by name or SKU
 * - Category, Color, Stock filters
 * - Infinite scroll pagination (10 products at a time)
 */
const InventorySelectionModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  selectedSku, 
  defaultGender = '',
  title = 'Select from Inventory',
  subtitle,
  itemsPerPage = 10,
  multiSelect = false,
  selectedProducts = []
}) => {
  // Data states
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState(defaultGender);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [colorFilter, setColorFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  
  // Filter options (populated from data)
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  
  // View state
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  // Refs
  const scrollContainerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Fetch products with pagination
  const fetchProducts = useCallback(async (page = 1, append = false) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const response = await inventoryAPI.listProducts({
        status: 'active',
        page: page,
        page_size: itemsPerPage,
        search: searchQuery || undefined
      });
      
      let newProducts = response.data.products || [];
      const total = response.data.total || 0;
      
      // Apply client-side filters for gender, category, color, stock
      // (these filters are applied client-side since the API doesn't support all of them)
      if (genderFilter) {
        newProducts = newProducts.filter(p => 
          (p.gender || '').toLowerCase() === genderFilter.toLowerCase()
        );
      }
      if (categoryFilter) {
        newProducts = newProducts.filter(p => 
          (p.category || '').toLowerCase() === categoryFilter.toLowerCase()
        );
      }
      if (colorFilter) {
        newProducts = newProducts.filter(p => 
          (p.color || '').toLowerCase() === colorFilter.toLowerCase()
        );
      }
      if (stockFilter === 'in_stock') {
        newProducts = newProducts.filter(p => (p.total_stock || 0) > 0);
      } else if (stockFilter === 'out_of_stock') {
        newProducts = newProducts.filter(p => (p.total_stock || 0) === 0);
      }
      
      // Update categories and colors for filter dropdowns (only on first page)
      if (page === 1) {
        const allProducts = response.data.products || [];
        const uniqueCategories = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
        const uniqueColors = [...new Set(allProducts.map(p => p.color).filter(Boolean))];
        setCategories(uniqueCategories);
        setColors(uniqueColors);
      }
      
      setTotalProducts(total);
      
      if (append) {
        setProducts(prev => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
      }
      
      // Check if there are more products to load
      const loadedCount = append ? products.length + newProducts.length : newProducts.length;
      setHasMore(loadedCount < total);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [itemsPerPage, searchQuery, genderFilter, categoryFilter, colorFilter, stockFilter, products.length]);

  // Load initial products when modal opens
  useEffect(() => {
    if (isOpen) {
      setProducts([]);
      setCurrentPage(1);
      setHasMore(true);
      fetchProducts(1, false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload products when filters change
  useEffect(() => {
    if (isOpen) {
      setProducts([]);
      setCurrentPage(1);
      setHasMore(true);
      fetchProducts(1, false);
    }
  }, [genderFilter, categoryFilter, colorFilter, stockFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setProducts([]);
      setCurrentPage(1);
      setHasMore(true);
      fetchProducts(1, false);
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset filters on modal close
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setGenderFilter(defaultGender);
      setCategoryFilter('');
      setColorFilter('');
      setStockFilter('');
      setShowFilters(false);
    }
  }, [isOpen, defaultGender]);

  // Infinite scroll handler
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    
    // Load more when user scrolls to bottom (with 100px threshold)
    if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
      fetchProducts(currentPage + 1, true);
    }
  }, [hasMore, loadingMore, loading, currentPage, fetchProducts]);

  const handleSelect = (product) => {
    onSelect(product);
    if (!multiSelect) {
      onClose();
    }
  };

  const isProductSelected = (product) => {
    if (multiSelect) {
      return selectedProducts.some(p => (p.product_id || p.id) === (product.product_id || product.id));
    }
    return selectedSku?.product_id === product.product_id;
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setGenderFilter('');
    setCategoryFilter('');
    setColorFilter('');
    setStockFilter('');
  };

  const activeFilterCount = [genderFilter, categoryFilter, colorFilter, stockFilter].filter(Boolean).length;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-blue-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
                <p className="text-xs text-slate-500">{subtitle || `${totalProducts} products available`}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="inventory-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search and Filters Bar */}
          <div className="p-4 border-b border-slate-100 space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                data-testid="inventory-search-input"
              />
            </div>

            {/* Filter Toggle & View Mode */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Filter Dropdowns */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
                    {/* Gender Filter */}
                    <select
                      value={genderFilter}
                      onChange={(e) => setGenderFilter(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="gender-filter"
                    >
                      <option value="">All Genders</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="unisex">Unisex</option>
                    </select>

                    {/* Category Filter */}
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="category-filter"
                    >
                      <option value="">All Categories</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>

                    {/* Color Filter */}
                    <select
                      value={colorFilter}
                      onChange={(e) => setColorFilter(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="color-filter"
                    >
                      <option value="">All Colors</option>
                      {colors.map(color => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>

                    {/* Stock Filter */}
                    <select
                      value={stockFilter}
                      onChange={(e) => setStockFilter(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="stock-filter"
                    >
                      <option value="">All Stock</option>
                      <option value="in_stock">In Stock</option>
                      <option value="out_of_stock">Out of Stock</option>
                    </select>
                  </div>
                  
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearAllFilters}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      Clear all filters
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Products Grid with Infinite Scroll */}
          <div 
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto p-4"
            onScroll={handleScroll}
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <p className="mt-2 text-sm text-slate-500">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Package className="w-12 h-12 text-slate-300" />
                <p className="mt-3 text-slate-500">No products found</p>
                {(searchQuery || activeFilterCount > 0) && (
                  <button
                    onClick={clearAllFilters}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className={
                  viewMode === 'grid' 
                    ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3'
                    : 'space-y-2'
                }>
                  {products.map((product) => {
                    const isSelected = isProductSelected(product);
                    const imageUrl = product.tryon_input_image_url || product.image_url || product.garment_url;
                    const name = product.product_name || product.name || product.sku_id;
                    const price = product.selling_price || product.mrp;
                    
                    if (viewMode === 'list') {
                      return (
                        <motion.button
                          key={product.product_id}
                          onClick={() => handleSelect(product)}
                          whileHover={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-blue-300'
                          }`}
                          data-testid={`product-item-${product.product_id}`}
                        >
                          <div className="w-14 h-14 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                            {imageUrl ? (
                              <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-6 h-6 text-slate-300" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{name}</p>
                            <p className="text-xs text-slate-500">{product.sku_id}</p>
                            {price && <p className="text-sm text-slate-600">₹{price.toLocaleString()}</p>}
                          </div>
                          {isSelected && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </motion.button>
                      );
                    }
                    
                    return (
                      <motion.button
                        key={product.product_id}
                        onClick={() => handleSelect(product)}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        className={`relative group text-left bg-white rounded-xl border-2 overflow-hidden transition-all ${
                          isSelected
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-slate-200 hover:border-blue-300 hover:shadow-lg'
                        }`}
                        data-testid={`product-item-${product.product_id}`}
                      >
                        <div className="aspect-[3/4] bg-slate-100 relative overflow-hidden">
                          {imageUrl ? (
                            <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-8 h-8 text-slate-300" />
                            </div>
                          )}
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="p-2.5">
                          <p className="text-xs font-medium text-slate-900 truncate">{name}</p>
                          {price && <p className="text-xs text-slate-500">₹{price.toLocaleString()}</p>}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
                
                {/* Loading more indicator */}
                {loadingMore && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                    <span className="ml-2 text-sm text-slate-500">Loading more...</span>
                  </div>
                )}
                
                {/* End of list indicator */}
                {!hasMore && products.length > 0 && (
                  <p className="text-center text-sm text-slate-400 py-4">
                    Showing all {products.length} products
                  </p>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {products.length} of {totalProducts} products loaded
              {activeFilterCount > 0 && ` (${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} applied)`}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
              data-testid="inventory-modal-cancel"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default InventorySelectionModal;
